import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { PaywallService } from '../../services/paywall.service';
import { CheckoutService } from '../../services/checkout.service';
import { contactSalesIncludesHtml, contactSalesSlaNote, contactSalesTitle } from '../../helpers/quantity.helper';
import { ContactSalesField, ContactSalesFieldKey, ContactSalesLead } from '../../models/paywall-settings';

/** What the form falls back to when the tenant has not configured fields (the pre-existing form). */
const LEGACY_FIELDS: ContactSalesField[] = [
  { key: 'email', label: 'Work email', enabled: true, required: true },
  { key: 'company', label: 'Company', enabled: true, required: false },
  { key: 'note', label: 'Tell us more', enabled: true, required: false }
];

const DEFAULT_LABELS: Record<ContactSalesFieldKey, string> = {
  email: 'Work email',
  firstName: 'First name',
  lastName: 'Last name',
  company: 'Company',
  phone: 'Phone',
  seats: 'Seats',
  note: 'Tell us more'
};

const AUTOCOMPLETE: Partial<Record<ContactSalesFieldKey, string>> = {
  email: 'email',
  firstName: 'given-name',
  lastName: 'family-name',
  company: 'organization',
  phone: 'tel'
};

@Component({
  selector: 'yon-pw-paywall-contact',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './paywall-contact.component.html',
  styleUrl: './paywall-contact.component.scss'
})
export class PaywallContactComponent {
  public paywallService = inject(PaywallService);
  private readonly checkoutService = inject(CheckoutService);

  public submitError = signal('');
  public submitting = signal(false);

  public contactSales = computed(() => {
    const data = this.paywallService.settings()?.contactSalesData;
    return {
      title: contactSalesTitle(data),
      subTitle: data?.subTitle ?? '',
      slaNote: contactSalesSlaNote(data),
      includesTitle: data?.includesTitle ?? '',
      includesHtml: contactSalesIncludesHtml(data)
    };
  });

  /** Enabled fields in configured order; email is always present so the lead can be identified. */
  public fields = computed<ContactSalesField[]>(() => {
    const configured = this.paywallService.settings()?.contactSalesData?.fields;
    const source = configured?.length ? configured : LEGACY_FIELDS;
    const enabled = source.filter(f => f.enabled && f.key in DEFAULT_LABELS);
    if (!enabled.some(f => f.key === 'email')) {
      enabled.unshift({ key: 'email', label: DEFAULT_LABELS.email, enabled: true, required: true });
    }
    return enabled.map(f => ({ ...f, required: f.key === 'email' ? true : f.required, label: f.label?.trim() || DEFAULT_LABELS[f.key] }));
  });

  public contactForm = signal(new FormGroup<Record<string, FormControl<string | number | null>>>({}));

  constructor() {
    effect(() => {
      this.buildForm(this.fields());
    });
  }

  private buildForm(fields: ContactSalesField[]): void {
    const group = new FormGroup<Record<string, FormControl<string | number | null>>>({});
    for (const field of fields) {
      const validators: ValidatorFn[] = [];
      if (field.required) { validators.push(Validators.required); }
      if (field.key === 'email') { validators.push(Validators.email); }
      if (field.key === 'seats') { validators.push(Validators.min(1)); }

      // Seats starts empty rather than with the quantity from the cards: Contact Sales is for buyers none of
      // the cards fit, so only what they type here goes on the lead.
      const initial: string | number | null = field.key === 'seats' ? null : '';
      group.addControl(field.key, new FormControl<string | number | null>(initial, validators));
    }
    this.contactForm.set(group);
  }

  inputType(field: ContactSalesField): string {
    switch (field.key) {
      case 'email': return 'email';
      case 'phone': return 'tel';
      case 'seats': return 'number';
      default: return 'text';
    }
  }

  autocomplete(field: ContactSalesField): string | null {
    return AUTOCOMPLETE[field.key] ?? null;
  }

  controlId(field: ContactSalesField): string {
    return `contact-${field.key}`;
  }

  /** Message to show under a field, once the buyer has left it or tried to submit. */
  fieldError(field: ContactSalesField): string | null {
    const control = this.contactForm().get(field.key);
    if (!control || control.valid || !control.touched) {
      return null;
    }
    if (control.hasError('required')) {
      return 'This field is required.';
    }
    if (control.hasError('email')) {
      return 'Enter a valid email address.';
    }
    if (control.hasError('min')) {
      return 'Enter at least 1 seat.';
    }
    return 'This value is not valid.';
  }

  goBack(): void {
    this.paywallService.step.set('cards');
  }

  submit(): void {
    if (this.paywallService.isPreview()) {
      return;
    }

    const form = this.contactForm();
    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }

    const value = form.getRawValue();
    const text = (key: ContactSalesFieldKey): string | undefined => {
      const raw = value[key];
      const trimmed = typeof raw === 'string' ? raw.trim() : raw?.toString();
      return trimmed ? trimmed : undefined;
    };
    const email = text('email') ?? '';
    const seatsRaw = value['seats'];
    const seats = seatsRaw != null && seatsRaw !== '' ? Number(seatsRaw) : undefined;

    this.submitting.set(true);
    this.submitError.set('');

    const lead: ContactSalesLead = {
      email,
      firstName: text('firstName'),
      lastName: text('lastName'),
      company: text('company'),
      phone: text('phone'),
      seats: seats && seats > 0 ? seats : undefined,
      note: text('note'),
      context: {
        pageUri: typeof location !== 'undefined' ? location.href : undefined,
        pageName: typeof document !== 'undefined' ? document.title : undefined,
        hutk: readCookie('hubspotutk')
      }
    };

    this.checkoutService.contactSales(lead).subscribe({
      next: () => {
        this.paywallService.contactEmail.set(email);
        this.paywallService.step.set('contactSuccess');
        this.submitting.set(false);
      },
      error: () => {
        this.submitError.set('Could not send your request. Please try again.');
        this.submitting.set(false);
      }
    });
  }
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined' || !document.cookie) { return undefined; }
  const match = document.cookie
    .split(';')
    .map(part => part.trim())
    .find(part => part.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.substring(name.length + 1)) : undefined;
}
